import type {
    ApolloClient,
    ApolloQueryResult,
    NormalizedCacheObject,
    TypedDocumentNode,
} from '@apollo/client';
import { isApolloError } from '@apollo/client';

import type { Exact, getHomeRecsQuery, getOmpDataQuery } from '../__generated__/graphql';

interface GdpQueryProps {
    homePagesGraphqlQuery: TypedDocumentNode<
        getHomeRecsQuery | getOmpDataQuery,
        Exact<{
            [key: string]: never;
        }>
    >;
    apolloClient: ApolloClient<NormalizedCacheObject>;
    variables?: Record<string, unknown>;
}

/**
 * Gets the collections for home recs and onsiteMessage for OMP homepage placements from GDP
 * @param  {DocumentNode} homePagesGraphqlQuery - homepage graphQL query
 * @param  {ApolloClient} apolloClient - gdp client
 * @returns {ApolloQueryResult<GetHomeRecsQuery>}
 *  GDP response containing collections + onsiteMessage data if no errors,
 *  else partial data with errors
 */
export async function fetchHomepageGdpData<Type>({
    homePagesGraphqlQuery,
    apolloClient,
    variables,
}: GdpQueryProps): Promise<ApolloQueryResult<Type> | null> {
    try {
        const gdpResponse = await apolloClient.query<Type>({
            query: homePagesGraphqlQuery,
            variables,
            // Added error policy to return partial data with errors from GDP
            errorPolicy: 'all',
        });
        // Logging GDP resolver errors returned along with partial data in the response
        if (gdpResponse.errors && gdpResponse.errors.length > 0) {
            gdpResponse.errors.forEach((error) => {
                const path = error.path?.join() ?? 'Unknown path';

                // eslint-disable-next-line no-console
                console.error(
                    `GraphQL Error fetching data from path: ${path} with message: ${error.message}`,
                    JSON.stringify(error)
                );
            });
        }
        return gdpResponse;
    } catch (error) {
        // GDP network errors and graphql validation errors are part of networkError
        if (isApolloError(error)) {
            // eslint-disable-next-line no-console
            console.error(
                'GDP query for Home Recs Collections/OMP failed due to network issues',
                JSON.stringify(error.networkError)
            );
        }
    }
    return null;
}
